import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useNavigate, useParams } from "react-router-dom";
import { API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Loader2, ArrowLeft, Wand2, ExternalLink, Download, Share2, Copy,
  ScanEye, CheckCircle2, AlertCircle, RefreshCw, User,
} from "lucide-react";

const API = `${API_BASE}/api`;

function errMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d.map((x) => x.msg).join("; ");
  return err?.message || fallback;
}

/* -------------------------------------------------------------------------- */
/* Intake form — used when the rep hits /new. Mirrors the Rep Input Block     */
/* copy exactly so reps who read the prompt doc find familiar field names.    */
/* -------------------------------------------------------------------------- */
export function CompetitorReportBuilderNewPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    business_name: "",
    website: "",
    industry: "",
    location: "",
    notable: "",
    competitor_urls: ["", "", ""],
    emphasis: "",
    client_id: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setUrl = (i, v) => setForm((f) => {
    const urls = [...f.competitor_urls];
    urls[i] = v;
    return { ...f, competitor_urls: urls };
  });

  // Pull the rep's book once so the client picker doesn't require typing.
  // Failure is silent — attach-to-client is optional and shouldn't block
  // creating a report.
  useEffect(() => {
    axios.get(`${API}/clients`).then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  // If the picked client has a name/site we can prefill the intake — the rep
  // is almost always running the report FOR that client, so re-typing is waste.
  const onClientPick = (cid) => {
    set("client_id", cid);
    if (!cid || cid === "none") { set("client_id", ""); return; }
    const c = clients.find((x) => x.id === cid);
    if (!c) return;
    setForm((f) => ({
      ...f,
      client_id: cid,
      business_name: f.business_name || c.business_name || c.name || "",
      website: f.website || c.website || "",
      industry: f.industry || c.industry || "",
      location: f.location || c.location || "",
    }));
  };

  const submit = async () => {
    if (!form.business_name.trim()) {
      toast.error("Business name is required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        client_id: form.client_id || null,
        competitor_urls: form.competitor_urls.filter((u) => u.trim()),
      };
      const r = await axios.post(`${API}/competitor-reports`, payload);
      toast.success("Report created — Claude is working on it");
      navigate(`/prospect-iq/competitor-reports/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e, "Could not create report"));
    } finally {
      setSubmitting(false);
    }
  };

  const filledUrls = form.competitor_urls.filter((u) => u.trim()).length;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="competitor-report-new">
      <Button asChild variant="ghost" size="sm" className="-ml-2">
        <Link to="/prospect-iq/competitor-reports">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to reports
        </Link>
      </Button>
      <div>
        <h1 className="font-outfit text-3xl font-bold tracking-tight flex items-center gap-2">
          <ScanEye className="h-7 w-7 text-indigo-600" /> New Competitor Report
        </h1>
        <p className="text-muted-foreground mt-1">
          Fill in what you know. Only the prospect&apos;s business name is required — Claude will find
          the top 3 competitors if you leave those blank.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-5">
          {clients.length > 0 && (
            <div>
              <Label>Attach to a client <span className="text-xs font-normal text-muted-foreground">(optional — prefills the intake and lets the report show on that client&apos;s page)</span></Label>
              <Select value={form.client_id || "none"} onValueChange={onClientPick}>
                <SelectTrigger data-testid="competitor-client-picker">
                  <SelectValue placeholder="Not attached to a client" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not attached —</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.business_name || c.name || "(no name)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Business name <span className="text-red-500">*</span></Label>
            <Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)}
                   placeholder="e.g. Anderson Roofing" data-testid="competitor-business-name" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Website (optional)</Label>
              <Input value={form.website} onChange={(e) => set("website", e.target.value)}
                     placeholder="https://…" data-testid="competitor-website" />
            </div>
            <div>
              <Label>Location / service area</Label>
              <Input value={form.location} onChange={(e) => set("location", e.target.value)}
                     placeholder="Raleigh, NC or national/online" data-testid="competitor-location" />
            </div>
          </div>
          <div>
            <Label>Industry / what they do</Label>
            <Input value={form.industry} onChange={(e) => set("industry", e.target.value)}
                   placeholder="Residential roofing" data-testid="competitor-industry" />
          </div>
          <div>
            <Label>Anything notable (optional)</Label>
            <Textarea value={form.notable} onChange={(e) => set("notable", e.target.value)}
                      rows={2} placeholder="Size, specialties, goals…" data-testid="competitor-notable" />
          </div>

          <div className="pt-2 border-t">
            <Label className="text-base">Competitors <span className="text-xs font-normal text-muted-foreground">— leave blank if unsure, Claude will find the top 3</span></Label>
            <div className="space-y-2 mt-2">
              {form.competitor_urls.map((u, i) => (
                <Input key={i} value={u} onChange={(e) => setUrl(i, e.target.value)}
                       placeholder={`Competitor ${i + 1} URL (optional)`}
                       data-testid={`competitor-url-${i}`} />
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {filledUrls === 3
                ? "All 3 URLs provided — Claude will skip discovery and go straight to the full report."
                : filledUrls === 0
                  ? "No URLs provided — Claude will find the top 3 competitors and ask you to confirm before building the deck."
                  : `${filledUrls} of 3 URLs provided — Claude will fill in the rest and ask you to confirm before building the deck.`}
            </p>
          </div>

          <div>
            <Label>Emphasis (optional)</Label>
            <Input value={form.emphasis} onChange={(e) => set("emphasis", e.target.value)}
                   placeholder='e.g. "focus on their advertising" or "reviews & reputation"'
                   data-testid="competitor-emphasis" />
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={submit} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="submit-competitor-report">
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Kicking off…</>
              ) : (
                <><Wand2 className="h-4 w-4 mr-2" /> Run the analysis</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Viewer — polls the report while it's discovering/generating, offers        */
/* competitor confirmation, and renders the HTML preview + PPTX download once */
/* it's ready.                                                                */
/* -------------------------------------------------------------------------- */
export default function CompetitorReportPage() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editingComps, setEditingComps] = useState(null); // draft edits pre-confirm
  const [confirming, setConfirming] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/competitor-reports/${id}`);
      setDoc(r.data);
      setLoadError(null);
    } catch (e) {
      const msg = errMsg(e, "Could not load report");
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  // Auto-poll while the report is being worked on
  useEffect(() => {
    if (!doc) return;
    if (!["discovering", "generating"].includes(doc.status)) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [doc?.status]);

  // Seed the pending-confirm editor when we first see the discovered candidates
  useEffect(() => {
    if (doc?.status === "pending_confirm" && !editingComps) {
      setEditingComps((doc.discovered_competitors || []).slice(0, 3));
    }
  }, [doc?.status, doc?.discovered_competitors]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (loadError || !doc) {
    // 403 / 404 / network failure — surface an explicit message with a way
    // back to the list instead of an empty page (was blank pre-fix).
    return (
      <div className="max-w-xl mx-auto py-16" data-testid="competitor-report-error">
        <Card className="border-2 border-red-200 bg-red-50/40">
          <CardContent className="py-8 text-center">
            <AlertCircle className="h-10 w-10 mx-auto text-red-500 mb-3" />
            <p className="font-semibold">Report unavailable</p>
            <p className="text-sm text-muted-foreground mt-1">
              {loadError || "This report could not be loaded — it may have been deleted or you may not have access."}
            </p>
            <Button asChild className="mt-4" variant="outline">
              <Link to="/prospect-iq/competitor-reports">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back to reports
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isBusy = ["discovering", "generating"].includes(doc.status);
  const shareUrl = doc.share_token
    ? `${API_BASE}/api/public/competitor-reports/${doc.share_token}`
    : "";
  const previewUrl = `${API}/competitor-reports/${doc.id}/preview`;
  const pptxUrl = `${API}/competitor-reports/${doc.id}/pptx`;

  const confirmAndRun = async () => {
    const comps = (editingComps || []).filter((c) => (c.url || "").trim());
    if (comps.length !== 3) {
      toast.error("Provide 3 competitors with URLs");
      return;
    }
    setConfirming(true);
    try {
      const r = await axios.post(`${API}/competitor-reports/${doc.id}/confirm-competitors`,
                                 { competitors: comps });
      setDoc(r.data);
      setEditingComps(null);
      toast.success("Building the deck — this typically takes 60-120 seconds");
    } catch (e) {
      toast.error(errMsg(e, "Confirm failed"));
    } finally {
      setConfirming(false);
    }
  };

  const rerun = async () => {
    if (!window.confirm("Re-run the analysis with the same 3 competitors? Claude will fetch fresh data (~90–120s).")) return;
    try {
      const r = await axios.post(`${API}/competitor-reports/${doc.id}/rerun`);
      setDoc(r.data);
      toast.success("Re-running — this typically takes 60–120 seconds");
    } catch (e) {
      toast.error(errMsg(e, "Re-run failed"));
    }
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="space-y-6" data-testid="competitor-report-viewer">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Button asChild variant="ghost" size="sm" className="mb-2 -ml-2">
            <Link to="/prospect-iq/competitor-reports">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to reports
            </Link>
          </Button>
          <h1 className="font-outfit text-3xl font-bold tracking-tight flex items-center gap-2">
            <ScanEye className="h-7 w-7 text-indigo-600" />
            {doc.business_name}
          </h1>
          <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
            {doc.location && <span>{doc.location}</span>}
            {doc.industry && <span>· {doc.industry}</span>}
            {doc.classification && <Badge variant="outline">{doc.classification.toUpperCase()}</Badge>}
            {doc.client_name && (
              <span className="inline-flex items-center gap-1 text-indigo-700">
                · <User className="h-3.5 w-3.5" /> {doc.client_name}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {doc.status === "ready" && (
            <>
              <Button asChild variant="outline" size="sm" data-testid="download-competitor-pptx">
                <a href={pptxUrl} download><Download className="h-4 w-4 mr-1" /> Download PPTX</a>
              </Button>
              <Button size="sm" variant="outline" onClick={rerun} data-testid="rerun-competitor-analysis">
                <RefreshCw className="h-4 w-4 mr-1" /> Re-run analysis
              </Button>
              <Button size="sm" variant="outline" onClick={copyShare} data-testid="copy-competitor-share">
                <Share2 className="h-4 w-4 mr-1" /> Share link
              </Button>
              <Button asChild size="sm" variant="outline" data-testid="open-competitor-preview">
                <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-1" /> Open in new tab
                </a>
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={load} data-testid="refresh-competitor-report">
            <RefreshCw className={`h-4 w-4 ${isBusy ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Status card */}
      {isBusy && (
        <Card className="border-2 border-indigo-200 bg-indigo-50/40">
          <CardContent className="py-4 flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
            <div>
              <p className="font-semibold text-sm text-indigo-900">
                {doc.status === "discovering"
                  ? "Finding the top 3 competitors…"
                  : "Building the full analysis…"}
              </p>
              <p className="text-xs text-muted-foreground">
                {doc.status === "discovering"
                  ? "Usually 10–20 seconds. Claude is searching Google Maps and the web for the closest matches."
                  : "Usually 60–120 seconds. Claude is browsing each competitor's site, ads, reviews, and social presence."}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {doc.status === "failed" && (
        <Card className="border-2 border-red-200 bg-red-50/40">
          <CardContent className="py-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-sm text-red-900">Generation failed</p>
              <p className="text-xs text-red-700 mt-0.5 break-all">{doc.error || "Unknown error"}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirm competitors panel */}
      {doc.status === "pending_confirm" && editingComps && (
        <Card className="border-2 border-amber-200 bg-amber-50/40" data-testid="confirm-competitors-panel">
          <CardContent className="pt-5 space-y-4">
            <div>
              <h2 className="font-semibold text-lg flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-amber-600" />
                Confirm the 3 competitors
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Claude picked these as the closest direct competitors. Swap any that aren&apos;t a
                real match before we run the full analysis.
              </p>
              {doc.reasoning && (
                <p className="text-xs text-muted-foreground mt-2 italic">Reasoning: {doc.reasoning}</p>
              )}
            </div>
            <div className="space-y-3">
              {editingComps.map((c, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1.5fr] gap-2 border rounded-lg p-3 bg-white">
                  <div>
                    <Label className="text-xs">Name</Label>
                    <Input
                      value={c.name || ""}
                      onChange={(e) => {
                        const next = [...editingComps];
                        next[i] = { ...c, name: e.target.value };
                        setEditingComps(next);
                      }}
                      data-testid={`confirm-comp-name-${i}`}
                    />
                    {c.why && <p className="text-[11px] text-muted-foreground mt-1 italic">{c.why}</p>}
                  </div>
                  <div>
                    <Label className="text-xs">URL</Label>
                    <Input
                      value={c.url || ""}
                      onChange={(e) => {
                        const next = [...editingComps];
                        next[i] = { ...c, url: e.target.value };
                        setEditingComps(next);
                      }}
                      placeholder="https://…"
                      data-testid={`confirm-comp-url-${i}`}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button onClick={confirmAndRun} disabled={confirming}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="confirm-competitors-run">
                {confirming ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Kicking off…</>
                ) : (
                  <><Wand2 className="h-4 w-4 mr-2" /> Confirm &amp; build the deck</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Share link banner */}
      {doc.status === "ready" && shareUrl && (
        <Card className="bg-emerald-50/50 border-emerald-200">
          <CardContent className="py-3 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <Share2 className="h-4 w-4 text-emerald-700" />
              <span className="text-emerald-900 font-medium">Public share link:</span>
              <code className="text-xs bg-white px-2 py-0.5 rounded border">{shareUrl}</code>
            </div>
            <Button size="sm" variant="outline" onClick={copyShare} data-testid="copy-share-inline">
              <Copy className="h-3.5 w-3.5 mr-1" /> Copy
            </Button>
          </CardContent>
        </Card>
      )}

      {/* HTML preview iframe — only mounted once the report is ready */}
      {doc.status === "ready" && (
        <div className="border rounded-xl overflow-hidden bg-white" data-testid="competitor-preview-frame">
          <iframe
            src={previewUrl}
            title="Competitive Analysis Preview"
            className="w-full"
            style={{ height: "80vh", border: "none" }}
          />
        </div>
      )}
    </div>
  );
}
